/** The two kinds of file the asset library holds. A picker shows one kind. */
export type AssetType = "IMAGE" | "FONT";

/** What the picker shows. Not a mirror of the asset service's response — see spec §6. */
export interface AssetSummary {
    id: string;
    displayName: string;
    /** Decides how the asset is drawn: a thumbnail for an image, an "Aa"
     *  specimen for a font. */
    type: AssetType;
    state: "PENDING" | "UPLOADING" | "FINALIZING" | "UPLOADED" | "READY" | "REJECTED" | "DELETING";
    /** For an image, a small rendition to show; for a font, the font file
     *  itself, which the specimen loads. Null until something is renderable. */
    thumbnailUrl: string | null;
    /** Intrinsic dimensions as delivered by the renderer; null until verified. */
    width: number | null;
    height: number | null;
    folderId: string | null;
}

/**
 * Flat (spec §5.1). A folder has no parent, every folder is a sibling, and the
 * root-to-leaf `path` and `depth` the first cut carried are gone with nesting —
 * a bare name is once again a complete identifier, because names are unique per
 * workspace.
 */
export interface AssetFolderSummary {
    id: string;
    name: string;
}

export type AssetRejectionReason =
    | "CONTENT_TYPE_MISMATCH"
    | "INVALID_CONTENT"
    | "IMAGE_DIMENSIONS_EXCEEDED"
    | "CONTENT_MISSING";

/**
 * Three honestly-labelled phases (spec §7). Byte transfer carries a real
 * percentage because it is measurable; verification carries none, because the
 * service exposes no progress signal for it and a bar there would be invented.
 */
export type UploadPhase =
    | { phase: "idle" }
    | { phase: "transferring"; fileName: string; sentBytes: number; totalBytes: number }
    | { phase: "checking"; fileName: string }
    | { phase: "rejected"; fileName: string; reason: AssetRejectionReason; detail?: string };

/**
 * Exactly one scope is active at a time (spec §5.2). This replaces the old
 * `folderIds: string[]` + `unfiled: boolean` pair, which could represent
 * "unfiled AND inside a folder" — a combination the old service answered with
 * nothing, silently. The reworked API would now answer that honestly, and the
 * UI still declines to offer it: one scope at a time is the simpler model for a
 * library capped at a hundred assets, where free-text search already reaches
 * across folders. Making it a discriminated value rather than a convention two
 * writers have to remember means the illegal pair is no longer representable.
 */
export type FolderScopeValue = { mode: "all" } | { mode: "unsorted" } | { mode: "folder"; folderId: string };

/**
 * Structural equality for a scope. `All` and `Unsorted` both carry no folder id,
 * so anything comparing ids alone would call them the same place.
 */
export function sameScope(a: FolderScopeValue, b: FolderScopeValue): boolean {
    if (a.mode !== b.mode) return false;
    if (a.mode === "folder" && b.mode === "folder") return a.folderId === b.folderId;
    return true;
}

export interface AssetSearchPage {
    items: AssetSummary[];
    /**
     * Total matching the query, not the page. The picker reads it to tell a
     * folder-delete confirmation how many assets the cascade will take (spec
     * §5.6) — the alternative was a second loader callback fetching a number
     * this response already carries.
     */
    totalCount: number;
    nextPageToken: string | null;
}

export interface AssetQuery {
    text: string;
    scope: FolderScopeValue;
    pageToken: string | null;
    /**
     * The picker owns this rather than the host. Eight assets are visible at a
     * standard laptop size (spec §11), so *which* eight decides whether the
     * picker works — and the asset service defaults to ascending id, which is
     * arbitrary from the operator's view. Carrying it in the query keeps the
     * requirement in the contract instead of in a host that can forget it.
     */
    sort: "newest";
}

/**
 * What a rename or create attempt reports back. Names are unique per workspace
 * case-insensitively, so "already exists" is an ordinary outcome rather than an
 * exception — and spec §5.5 puts the message inline in the field that caused it
 * rather than in a toast. A rejected promise could not do that: this family owns
 * no error UI and a rejection would surface as an unhandled rejection with no
 * visible change. Host callbacks in this family never reject; they resolve with
 * a result.
 */
export type NameResult = { ok: true } | { ok: false; message: string };

/**
 * Create additionally hands back the folder it made, because two callers need
 * its id immediately: "create and move" (spec §5.7) and the upload target
 * select (§5.4). Without the id they would have to re-list folders and match on
 * a name they just typed.
 */
export type CreateFolderResult = { ok: true; folder: AssetFolderSummary } | { ok: false; message: string };

export type AssetLoader = (query: AssetQuery) => Promise<AssetSearchPage>;
export type FolderLoader = () => Promise<AssetFolderSummary[]>;
